/**
 * Utility for registering client-side scripts that need to run after Angular SSR hydration.
 *
 * This is necessary because Angular's lifecycle hooks don't always run reliably on the client
 * after SSR hydration, especially for event listeners attached to DOM elements.
 *
 * Usage in Angular components:
 * ```typescript
 * import { registerClientScript } from '@absolutejs/absolute';
 *
 * // Register an event listener script
 * registerClientScript(() => {
 *   const element = document.querySelector('.my-element');
 *   if (element) {
 *     element.addEventListener('click', () => {
 *       console.log('Clicked!');
 *     });
 *   }
 * });
 * ```
 *
 * The script will be automatically injected into the HTML response and executed on the client.
 */

// Request-scoped registry for client scripts
// Each request gets its own set of scripts to inject
const scriptRegistry = new Map<string, Set<() => void>>();

// Generate a unique request ID for tracking scripts per request
let requestCounter = 0;
const getRequestId = () => `req_${Date.now()}_${++requestCounter}`;

// Allow SSR frameworks to inject a request context getter (e.g. AsyncLocalStorage)
let ssrContextGetter: (() => string | undefined) | null = null;
export const getSsrContextId = () =>
	ssrContextGetter?.() ||
	Object.getOwnPropertyDescriptor(globalThis, '__absolutejs_requestId')
		?.value;
export const registerClientScript = (
	script: () => void,
	requestId?: string
) => {
	// Try to get requestId from explicit arg, then Async Context, then global fallback
	const id = requestId || getSsrContextId() || getRequestId();

	if (!scriptRegistry.has(id)) {
		scriptRegistry.set(id, new Set());
	}

	scriptRegistry.get(id)?.add(script);

	return id;
};
export const setSsrContextGetter = (getter: () => string | undefined) => {
	ssrContextGetter = getter;
};

// Make registerClientScript available globally during SSR for Angular components
// Using type assertion for globalThis extension
if (typeof globalThis !== 'undefined') {
	Object.assign(globalThis, { registerClientScript });
}

/**
 * Get all registered scripts for a request and clear them.
 * This is called by the page handler after rendering.
 *
 * @param requestId - The request ID to get scripts for
 * @returns Array of script functions, or empty array if none registered
 */
export const clearAllClientScripts = () => {
	scriptRegistry.clear();
};
export const generateClientScriptCode = (scripts: (() => void)[]) => {
	if (scripts.length === 0) {
		return '';
	}

	// Convert functions to strings and wrap in IIFE
	const scriptCode = scripts
		.map((script, index) => {
			// Get the function body as a string
			const funcString = script.toString();

			// Extract the body (everything between { and })
			const bodyMatch = funcString.match(/\{([\s\S]*)\}/);
			if (!bodyMatch || !bodyMatch[1]) {
				return '';
			}

			const body = bodyMatch[1].trim();

			// Wrap in IIFE with MutationObserver for DOM readiness
			return `
	(function() {
		var executed = false;
		function executeScript_${index}() {
			if (executed) return;
			executed = true;
			${body}
		}

		if (document.readyState === 'complete' || document.readyState === 'interactive') {
			executeScript_${index}();
		} else {
			document.addEventListener('DOMContentLoaded', executeScript_${index});
		}

		// Watch for hydration-added elements
		var observer = new MutationObserver(function() {
			executeScript_${index}();
			if (executed) observer.disconnect();
		});
		if (!executed) {
			observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
		}

		// Single fallback timeout
		setTimeout(function() {
			executeScript_${index}();
			observer.disconnect();
		}, 1000);
	})();`;
		})
		.join('\n');

	return `<script>
(function() {
${scriptCode}
})();
</script>`;
};
export const getAndClearClientScripts = (requestId?: string) => {
	const id = requestId || ssrContextGetter?.();
	if (!id) return [];

	const scripts = scriptRegistry.get(id);
	if (!scripts) {
		return [];
	}

	const scriptArray = Array.from(scripts);
	scriptRegistry.delete(id);

	return scriptArray;
};
